import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Zap, AlertCircle, AlertTriangle, Gauge, TrendingUp, Mountain, Route, CarFront, RotateCcw } from 'lucide-react';
import {
  FIELD_SPECS, SPEEDS_CAPPED_BY_MAX, MOTOR_PRESETS, DEFAULT_PRESET_ID, findPreset, validateField,
} from '../../lib/evMotorTools';
import { motorToolApi } from '../../lib/toolsApi';

const CALC_DEBOUNCE_MS = 350;

const SPEC_BY_KEY = Object.fromEntries(FIELD_SPECS.map((s) => [s.key, s]));

const GROUPS = [
  { id: 'vehicle', title: 'Vehicle & Payload', keys: ['vehicleMassKg', 'payloadMassKg', 'rotationalMassFactor'] },
  { id: 'aero', title: 'Aerodynamics & Rolling Resistance', keys: ['dragCoefficient', 'frontalAreaM2', 'airDensityKgM3', 'rollingResistanceCoefficient'] },
  { id: 'traction', title: 'Traction Limit', keys: ['tireFrictionCoefficient', 'driveAxleLoadShare'] },
  { id: 'performance', title: 'Performance Targets (Peak)', keys: ['maxSpeedKmph', 'accelTargetSpeedKmph', 'accelTimeS', 'constantPowerSpeedRatio'] },
  { id: 'duty', title: 'Duty Targets (Continuous & Hill Start)', keys: ['continuousSpeedKmph', 'sustainedGradePercent', 'sustainedGradeSpeedKmph', 'hillStartGradePercent'] },
  { id: 'drivetrain', title: 'Drivetrain & Electrical', keys: ['wheelRadiusM', 'gearRatio', 'gearboxEfficiency', 'motorInverterEfficiency', 'dcBusVoltageV'] },
  { id: 'margin', title: 'Sizing Margin', keys: ['safetyMarginPercent'] },
];

const SCENARIO_ICON = {
  'Continuous cruise': Route,
  'Sustained grade climb': Mountain,
  'Top-speed cruise': Gauge,
  'Hill start': Mountain,
};

function toRawStrings(defaults) {
  return Object.fromEntries(Object.entries(defaults).map(([k, v]) => [k, String(v)]));
}

const kw = (n) => `${n.toFixed(1)} kW`;

const Stat = ({ label, value, note, className = '' }) => (
  <div className={className}>
    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">{label}</p>
    <p className="font-mono text-sm font-bold">{value}</p>
    {note && <p className="text-[11px] text-muted-foreground mt-0.5">{note}</p>}
  </div>
);

const ScenarioRow = ({ s }) => {
  const isAccel = !s.forcesN;
  const Icon = isAccel ? TrendingUp : (SCENARIO_ICON[s.scenario] || Gauge);
  return (
    <div className="space-y-2 pb-4 border-b border-structure last:border-0 last:pb-0">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-autoxium-blue shrink-0" />
        <span className="font-chivo text-xs font-bold uppercase tracking-wider">{s.scenario}</span>
        <span className="font-mono text-[11px] text-muted-foreground ml-auto">
          {isAccel ? `t = ${s.simulatedTimeS ?? '—'} s` : `@ ${s.speedKmph} km/h${s.gradePercent ? `, ${s.gradePercent}%` : ''}`}
        </span>
      </div>
      {isAccel ? (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground pl-6">
          <span>Base speed: {s.baseVehicleSpeedKmph} km/h</span>
          <span>Closed form: {kw(s.closedFormPowerKw)}</span>
          <span>Launch force: {s.launchForceN} N</span>
          <span>Grip limit: {s.gripForceN} N{s.gripLimited ? ' (reached)' : ''}</span>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-1 font-mono text-xs text-muted-foreground pl-6">
          <span>Rolling: {s.forcesN.rolling} N</span>
          <span>Aero: {s.forcesN.aero} N</span>
          <span>Grade: {s.forcesN.grade} N</span>
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-x-2 font-mono text-sm pl-6">
        <span className="text-muted-foreground">Shaft power / torque / speed</span>
        <span className="font-bold">{kw(s.motorPowerKw)} · {s.motorTorqueNm} Nm · {s.motorRpm} RPM</span>
      </div>
    </div>
  );
};

const EvMotorSizingCalculator = () => {
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const preset = findPreset(presetId);
  const [raw, setRaw] = useState(() => toRawStrings(preset.inputs));

  const setField = (key, value) => setRaw((prev) => ({ ...prev, [key]: value }));

  // Loads every field from the preset in one go; editing any field afterwards keeps the
  // preset selected but marks it "(inputs modified)" with a Reset button.
  const loadPreset = (id) => {
    const p = findPreset(id);
    if (!p) return;
    setPresetId(id);
    setRaw(toRawStrings(p.inputs));
  };
  const presetModified = JSON.stringify(raw) !== JSON.stringify(toRawStrings(preset.inputs));

  const { fieldErrors, values } = useMemo(() => {
    const errors = {};
    const parsed = {};
    for (const spec of FIELD_SPECS) {
      const err = validateField(spec, raw[spec.key]);
      if (err) errors[spec.key] = err;
      else parsed[spec.key] = Number(raw[spec.key]);
    }
    // Cross-field validation, shown on both fields involved.
    if (!errors.maxSpeedKmph) {
      for (const [key, label] of SPEEDS_CAPPED_BY_MAX) {
        if (!errors[key] && parsed[key] > parsed.maxSpeedKmph) {
          errors[key] = `${label} cannot exceed max speed.`;
          errors.maxSpeedKmph = `Max speed must be at least the ${label.toLowerCase()}.`;
        }
      }
    }
    return { fieldErrors: errors, values: parsed };
  }, [raw]);

  const hasErrors = Object.keys(fieldErrors).length > 0;

  const [results, setResults] = useState(null);
  const [calcError, setCalcError] = useState(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    if (hasErrors) {
      setResults(null);
      setCalcError(null);
      return;
    }
    const requestId = ++requestIdRef.current;
    const timer = setTimeout(() => {
      motorToolApi.sizeMotor(values)
        .then((outcome) => {
          if (requestId !== requestIdRef.current) return;
          if (outcome.errors) {
            setResults(null);
            setCalcError(Object.values(outcome.errors)[0]);
          } else {
            setResults(outcome.results);
            setCalcError(null);
          }
        })
        .catch((e) => {
          if (requestId !== requestIdRef.current) return;
          setResults(null);
          setCalcError(e.message);
        });
    }, CALC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [values, hasErrors]);

  const renderField = (key) => {
    const spec = SPEC_BY_KEY[key];
    const error = fieldErrors[key];
    return (
      <div className="space-y-2" key={key}>
        <Label htmlFor={`motor-${key}`} className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
          {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
        </Label>
        <Input
          id={`motor-${key}`}
          type="number"
          step="any"
          value={raw[key]}
          onChange={(e) => setField(key, e.target.value)}
          className="font-mono"
          aria-invalid={!!error}
          aria-describedby={error ? `motor-${key}-error` : undefined}
          data-testid={`motor-${key}-input`}
        />
        {error && (
          <p id={`motor-${key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`motor-${key}-error`}>
            <AlertCircle className="h-3 w-3 shrink-0" /> {error}
          </p>
        )}
      </div>
    );
  };

  const rec = results?.recommendation;
  const margin = rec ? `w/ ${rec.safetyMarginPercent}% margin` : '';

  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader>
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2">
            <Zap className="h-5 w-5 text-autoxium-blue" />
            Electric Motor Sizing Calculator
          </CardTitle>
          <CardDescription>
            Estimate the traction motor's continuous and peak shaft power, torque, base and
            maximum speed, and the DC and phase currents it draws, from vehicle mass,
            aerodynamics, traction limit, performance and duty targets.
            Results update live as you edit.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 space-y-2" data-testid="motor-preset-panel">
            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
              <div className="space-y-1 flex-1 min-w-0 sm:max-w-md">
                <Label className="font-mono text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
                </Label>
                <Select value={presetId} onValueChange={loadPreset}>
                  <SelectTrigger className="h-9 text-sm" data-testid="motor-preset-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MOTOR_PRESETS.map((p) => (
                      <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {presetModified && (
                <Button variant="outline" size="sm" className="h-9 shrink-0" onClick={() => loadPreset(presetId)} data-testid="motor-preset-reset">
                  <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                </Button>
              )}
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              {preset.reference && (
                <p className="font-mono text-foreground/80" data-testid="motor-preset-reference">
                  Real vehicle: {preset.reference}
                  {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
                </p>
              )}
              <p data-testid="motor-preset-note">{preset.note}</p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {GROUPS.map((group) => (
            <div key={group.id} className="space-y-4 p-4 bg-muted/50 rounded-md">
              <h3 className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                {group.title}
              </h3>
              <div className="grid sm:grid-cols-2 gap-4">
                {group.keys.map(renderField)}
              </div>
            </div>
          ))}
          </div>
        </CardContent>
      </Card>

      <div className="space-y-6 min-w-0">
        {results?.warnings?.length > 0 && (
          <div className="space-y-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 p-3" data-testid="motor-warnings">
            {results.warnings.map((w, i) => (
              <p key={i} className="flex items-start gap-1.5 text-xs text-amber-800 dark:text-amber-300 leading-relaxed">
                <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" /> {w}
              </p>
            ))}
          </div>
        )}

        <Card className="technical-card h-fit">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-base">Recommended Motor Rating</CardTitle>
          </CardHeader>
          <CardContent>
            {hasErrors ? (
              <p className="text-sm text-muted-foreground" data-testid="motor-results-placeholder">
                Fix the highlighted fields to see results.
              </p>
            ) : calcError ? (
              <div className="flex items-center gap-2 text-sm text-destructive" data-testid="motor-calc-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {calcError}
              </div>
            ) : !results ? (
              <p className="text-sm text-muted-foreground" data-testid="motor-results-loading">
                Calculating…
              </p>
            ) : (
              <div className="space-y-4" data-testid="motor-results">
                {preset.reference && (
                  <p className="font-mono text-xs text-muted-foreground" data-testid="motor-results-reference">
                    Compare with the real {preset.label}: {preset.reference}
                    {presetModified && <span className="ml-2 text-amber-600 dark:text-amber-400">(inputs modified)</span>}
                  </p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Peak Power</p>
                    <p className="font-mono text-lg font-bold text-autoxium-blue" data-testid="motor-peak-power">{kw(rec.peakPowerKw)}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{margin} · {rec.peakPowerSourceScenario}</p>
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground mb-1">Peak Torque</p>
                    <p className="font-mono text-lg font-bold text-autoxium-blue" data-testid="motor-peak-torque">{rec.peakTorqueNm} Nm</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">{margin} · {rec.peakTorqueSourceScenario}</p>
                  </div>
                  <Stat label="Continuous Power (S1)" value={kw(rec.continuousPowerKw)} note={`${margin} · ${rec.continuousPowerSourceScenario}`} />
                  <Stat label="Continuous Torque (S1)" value={`${rec.continuousTorqueNm} Nm`} note={margin} />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t border-structure">
                  <Stat label="Base Speed" value={`${rec.baseSpeedMotorRpm} RPM`} note="Peak power ÷ peak torque" />
                  <Stat label="Max Speed" value={`${rec.maxSpeedMotorRpm} RPM`} note="At max vehicle speed" />
                  <Stat label="Speed Ratio" value={`${rec.constantPowerSpeedRatio.toFixed(2)} : 1`} note="Max ÷ base (field weakening)" />
                  <Stat
                    label="Simulated 0-Target"
                    value={rec.simulatedAccelTimeS != null ? `${rec.simulatedAccelTimeS.toFixed(2)} s` : '—'}
                    note="With the rated motor above"
                  />
                </div>
                <div className="pt-4 border-t border-structure space-y-3">
                  <p className="flex items-center gap-1.5 font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground">
                    <Zap className="h-3.5 w-3.5" /> Electrical @ {values.dcBusVoltageV} V DC
                  </p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Stat label="Peak DC Input" value={`${kw(rec.peakDcPowerKw)} · ${rec.peakDcCurrentA} A`} />
                    <Stat label="Cont. DC Input" value={`${kw(rec.continuousDcPowerKw)} · ${rec.continuousDcCurrentA} A`} />
                    <Stat label="Peak Phase Current" value={`≈ ${rec.peakPhaseCurrentArms} A rms`} />
                    <Stat label="Cont. Phase Current" value={`≈ ${rec.continuousPhaseCurrentArms} A rms`} />
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {results && (
          <Card className="technical-card" data-testid="motor-scenario-breakdown">
            <CardHeader>
              <CardTitle className="font-chivo uppercase tracking-tight text-base">Scenario Breakdown</CardTitle>
              <CardDescription>
                Shaft figures before margin. The continuous rating is the worst sustained
                scenario; the peak ratings are the worst of all scenarios, for power and
                torque independently.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid lg:grid-cols-2 gap-6">
              {[['continuous', 'Continuous (S1) duty'], ['peak', 'Peak (short-time) duty']].map(([duty, title]) => (
                <div key={duty} className="space-y-4">
                  <h4 className="font-chivo text-[11px] font-bold uppercase tracking-wider text-muted-foreground">{title}</h4>
                  {results.scenarios.filter((s) => s.duty === duty).map((s) => <ScenarioRow key={s.scenario} s={s} />)}
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card className="technical-card" data-testid="motor-sources">
          <CardHeader>
            <CardTitle className="font-chivo uppercase tracking-tight text-sm">Sources &amp; Assumptions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs text-muted-foreground leading-relaxed">
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">method</Badge>
              Acceleration is sized for a constant-torque / constant-power motor (Ehsani, Gao &amp;
              Emadi, <em>Modern Electric, Hybrid Electric, and Fuel Cell Vehicles</em>, ch. 4):
              P = δm/(2t)·(V_f² + V_b²) + ⅔·m·g·Crr·V_f + ⅕·ρ·Cd·A·V_f³, with V_b = max speed ÷
              speed ratio. The result is then checked by simulating the motor from standstill
              (tire grip included) and adjusting peak power until the target time is met.
              Steady scenarios use F = F_rolling + F_aero + F_grade, P = F·v, T = F·r / (G·η_gear).
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">duty</Badge>
              Continuous (S1) is the worse of cruise at the continuous speed and the sustained
              grade climb. Top speed is treated as short-time duty, reached in the constant-power
              region. Peak ratings are never below continuous. The safety margin is applied
              equally to all four ratings.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              All power and torque figures are at the motor shaft; only gearbox loss (default
              0.97) sits between shaft and wheel. Motor + inverter efficiency (default 0.90,
              around the peak-power corner) only converts shaft power to DC input. Phase current
              is approximate: it assumes SVPWM at the linear-modulation limit (V_LL,rms = V_dc/√2)
              and a 0.9 power factor at the corner point, and scales with torque below it.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle border-amber-500/50 text-amber-700">verify</Badge>
              Traction is capped at μ × m × g × drive-axle load share. Default μ 0.9 (dry asphalt)
              with 0.55 share suits a front-drive car; use about 0.6 for rear-drive (weight
              transfers rearward under acceleration) and 1.0 for all-wheel drive. The default
              0.30 Cd and 0.015 Crr are conservative; use your vehicle's published values.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">source</Badge>
              δ = 1.05 is typical of single-speed passenger EVs (1.03-1.10). A speed ratio of 3 is
              typical of PMSM/IPM traction motors (2.5-4). Reduction ratios of production EVs
              run about 7-10:1 (Chevy Bolt ≈7.05:1, Tesla Model 3 ≈9:1). Validated against the
              Nissan Leaf 40 kWh, Chevy Bolt, Hyundai Kona 64 kWh and Tesla Model 3 SR+: peak
              power and torque within about ±10% of published ratings, except the Model 3's
              torque (≈15% low), whose top speed is electronically limited.
            </div>
            <div>
              <Badge variant="outline" className="font-mono text-[10px] mr-1.5 align-middle">limits</Badge>
              A first-pass sizing estimate, not a motor design. It does not model thermal
              behaviour, efficiency maps, battery voltage sag, regenerative braking, or a
              multi-speed gearbox.
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default EvMotorSizingCalculator;
